import React, { CSSProperties, useEffect, useState } from 'react';
import { Handle, Position, Node, HandleProps, IsValidConnection, NodeResizer, OnResize, useNodes } from '@xyflow/react';
import { HidroDXEditor } from '../edit';
import { Divider, IconButton, MenuItem, Popover, Tooltip, Typography } from '@mui/material';
import { ArrowDownward, ArrowUpward, DeleteForever, Edit, RefreshRounded, TableChart } from '@mui/icons-material';
import { ColorPicker } from '../modals';
import { objectToHash } from '../../core';
import { MyNodeType } from '../../pages/Flow';
import { TableNodeData } from './tableNode';
import { BaseNodeData } from './baseNode';

export interface LegendNodeRow {
  hash: string;
  title: string;
  isTable?: boolean;
  bgColor?: string;
  border?: string | number;
  borderRadius?: string | number;
  clipPath?: string;
}

export interface LegendNodeStyle {
  legendStyle?: CSSProperties;
  rows?: LegendNodeRow[];
}

export type LegendNodeData = {
  lock?: boolean;
  markdown?: string;
  handles: HandleProps[];
  style: LegendNodeStyle;
  onChange?: (newLegend: Node<LegendNodeData>) => void;
}

const handleSize = 10;
const defaultLegendaWidth = 250;
const defaultLegendaHeight = 350;
const targetColor = "rgb(2, 41, 53)";
const sourceColor = "rgb(0, 153, 102)";

export function LegendNode(nodeData: Node<LegendNodeData>) {

  const nodes = useNodes<Node<MyNodeType>>();

  const [editRef, setEditRef] = useState<HTMLButtonElement>();
  const [colorPickerEl, setColorPickerEl] = useState<HTMLButtonElement>();
  const [legendStyle, setLegendStyle] = useState<LegendNodeData>(nodeData.data);

  // Carregando os dados na legenda a primeira vez
  useEffect(() => {
    if (!legendStyle.style.rows) {
      handleRefreshLegend();
    }
  }, []);

  const handleOpenColorPicker = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    setColorPickerEl(ev.currentTarget);
  };

  // Resize Legend
  const handleResize: OnResize = (_, params) => {
    const newStyle = {
      ...legendStyle,
      style: {
        ...legendStyle.style,
        legendStyle: {
          ...legendStyle.style.legendStyle,
          width: params.width,
          height: params.height,
        }
      }
    };

    setLegendStyle(newStyle);
    if (legendStyle.onChange) legendStyle.onChange({
      ...nodeData,
      data: newStyle,
    });
  };

  const handleRefreshLegend = () => {
    const rows: LegendNodeRow[] = [];
    const hashs: string[] = [];

    for (const node of nodes) {
      if (node.type === 'legendNode') continue;

      let data: Omit<Omit<LegendNodeRow, "hash">, "title">;

      if (node.type === "tableNode") {
        const tableStyle = (node.data as TableNodeData).style.tableStyle;
        data = {
          isTable: true,
          border: tableStyle.border,
          clipPath: tableStyle.clipPath,
          bgColor: tableStyle.backgroundColor,
          borderRadius: tableStyle.borderRadius,
        };
      } else { //(node.type === "baseNode") {
        const nodeStyle = (node.data as BaseNodeData).style;
        data = {
          isTable: false,
          border: `${nodeStyle.borderWidth ?? 1}px ${nodeStyle.borderStyle ?? "solid"} ${nodeStyle.borderColor ?? "black"}`,
          clipPath: nodeStyle.clipPath,
          bgColor: nodeStyle.backgroundColor,
          borderRadius: nodeStyle.borderRadius,
        };
      }

      const hash = objectToHash(data);

      // Conferindo se o nó já está adicionado
      if (hashs.includes(hash)) continue;
      hashs.push(hash);

      const props: LegendNodeRow = {
        hash,
        title: "",
        ...data,
      };

      rows.push(props);
    }

    const newStyle = {
      ...legendStyle,
      style: {
        ...legendStyle.style,
        rows: rows,
      }
    };

    setLegendStyle(newStyle);
    if (legendStyle.onChange) legendStyle.onChange({
      ...nodeData,
      data: newStyle,
    });
  };

  const handleMoveItem = (index: number, direction: 'up' | 'down') => {
    const rows = legendStyle.style.rows ?? [];

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= rows.length) return;

    const newArray = [...rows];
    // Remove o item da posição original
    const [movedItem] = newArray.splice(index, 1);
    // Insere na nova posição
    newArray.splice(newIndex, 0, movedItem);

    const newStyle = {
      ...legendStyle,
      style: {
        ...legendStyle.style,
        rows: newArray,
      }
    };

    setLegendStyle(newStyle);
    if (legendStyle.onChange) legendStyle.onChange({
      ...nodeData,
      data: { ...newStyle },
    });
  };

  const handleRemoveItem = (index: number) => {
    const rows = legendStyle.style.rows ?? [];

    const newArray = [...rows];
    newArray.splice(index, 1);

    const newStyle = {
      ...legendStyle,
      style: {
        ...legendStyle.style,
        rows: newArray,
      }
    };

    setLegendStyle(newStyle);
    if (legendStyle.onChange) legendStyle.onChange({
      ...nodeData,
      data: { ...newStyle },
    });
  };

  const handleEditItem = (index: number, title: string) => {
    const rows = legendStyle.style.rows ?? [];

    const newArray = [...rows];
    newArray[index] = {
      ...newArray[index],
      title: title,
    };

    const newStyle = {
      ...legendStyle,
      style: {
        ...legendStyle.style,
        rows: newArray,
      }
    };

    setLegendStyle(newStyle);
    if (legendStyle.onChange) legendStyle.onChange({
      ...nodeData,
      data: { ...newStyle },
    });
  };

  const validateConnection: IsValidConnection = (conn) => {
    if (conn.source === conn.target) return false;
    return true;
  };

  const text = legendStyle.markdown;
  const handles = legendStyle.handles;
  const locked = legendStyle.lock ?? false;
  const rows = legendStyle.style.rows ?? [];
  const style = legendStyle.style.legendStyle;
  const totalWidth = Number(legendStyle.style.legendStyle?.width) ?? defaultLegendaWidth;
  const totalHeight = Number(legendStyle.style.legendStyle?.height) ?? defaultLegendaHeight;


  const topHandles: HandleProps[] = handles.filter(val => val.position === Position.Top);
  const bottomHandles: HandleProps[] = handles.filter(val => val.position === Position.Bottom);
  const leftHandles: HandleProps[] = handles.filter(val => val.position === Position.Left);
  const rightHandles: HandleProps[] = handles.filter(val => val.position === Position.Right);

  return (
    <>
      {editRef &&
        <Popover
          open={Boolean(editRef)}
          anchorEl={editRef}
          onClose={() => setEditRef(undefined)}
          sx={{ width: "556px" }}
        >
          {/* Seletor de Cor */}
          <MenuItem disableRipple sx={{ flexDirection: 'row', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Cor de Fundo
            </Typography>
            <IconButton onClick={handleOpenColorPicker}>
              <div style={{
                width: "15px",
                height: "15px",
                border: "1px solid black",
              }} />
            </IconButton>
          </MenuItem>

          <HidroDXEditor
            startText={text}
            onChange={(text) => {
              const newStyle = {
                ...legendStyle,
                markdown: text,
              };

              setLegendStyle(newStyle);

              if (legendStyle.onChange) legendStyle.onChange({
                ...nodeData,
                data: newStyle
              });
            }}
          />
        </Popover>
      }

      {/* BG color */}
      {colorPickerEl &&
        <ColorPicker
          open={Boolean(colorPickerEl)}
          element={colorPickerEl}
          initialColor={style?.backgroundColor}
          onClose={() => setColorPickerEl(undefined)}
          onColor={(color) => {
            const newStyle = {
              ...legendStyle,
              style: {
                ...legendStyle.style,
                legendStyle: {
                  ...legendStyle.style.legendStyle,
                  backgroundColor: color,
                }
              },
            };

            setLegendStyle(newStyle);
            if (legendStyle.onChange) legendStyle.onChange({
              ...nodeData,
              data: newStyle,
            });
          }}
        />
      }

      <div key={`${locked}`}>
        {/* Handle Top */}
        {topHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: handleSize,
            height: val.type === 'source' ? handleSize : handleSize / 1.5,
            left: ((idx + 1) * (totalWidth / (topHandles.length + 1)))
          };
          return <Handle
            key={`top-${idx}`}
            {...val}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}

        {/* Handle Bottom */}
        {bottomHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: handleSize,
            height: val.type === 'source' ? handleSize : handleSize / 1.5,
            left: ((idx + 1) * (totalWidth / (bottomHandles.length + 1)))
          };

          return <Handle
            key={`bottom-${idx}`}
            {...val}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}

        {/* Left Handles */}
        {leftHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: val.type === 'source' ? handleSize : handleSize / 1.5,
            height: handleSize,
            top: ((idx + 1) * (totalHeight / (leftHandles.length + 1)))
          };

          return <Handle
            key={`left-${idx}`}
            {...val}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}

        {/* Right Handles */}
        {rightHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: val.type === 'source' ? handleSize : handleSize / 1.5,
            height: handleSize,
            top: ((idx + 1) * (totalHeight / (rightHandles.length + 1)))
          };

          return <Handle
            key={`right-${idx}`}
            {...val}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}

        <NodeResizer
          color='#000a'
          isVisible={!locked}
          minWidth={5}
          minHeight={5}
          onResize={handleResize}
        />

        <div style={{
          width: totalWidth,
          height: totalHeight,
          display: "flex",
          overflow: "visible",
          position: "relative",
          flexDirection: "column",
          border: "1px solid black",
          ...style,
        }}>

          {/* Editar o título e dar refresh nas camadas */}
          {!locked &&
            <div style={{
              transform: 'scale(0.6)',
              transformOrigin: 'center',
              position: 'absolute',
              top: '-35px',
              right: 0,
              width: `${38 * 2}px`,
              height: '35px',
              backgroundColor: "#000",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "15px",
              zIndex: 100,
            }}>
              <Tooltip title="Atualizar Legenda" >
                <IconButton
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    handleRefreshLegend();
                  }}
                  size='small'
                  sx={{ color: "#FFF" }}
                >
                  <RefreshRounded fontSize='small' />
                </IconButton>
              </Tooltip>
              <Tooltip title="Editar Título" >
                <IconButton
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    setEditRef(ev.currentTarget);
                  }}
                  size='small'
                  sx={{ color: "#FFF" }}
                >
                  <Edit fontSize='small' />
                </IconButton>
              </Tooltip>
            </div>
          }

          {text && <HidroDXEditor
            key={`${text.length}-${text.slice(Math.max(text.length - 50, 0), text.length)}`}
            readOnly
            startText={text}
          />}
          {text && <Divider />}

          <div style={{
            flex: 1,
            padding: "15px",
            display: "flex",
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}>
            {rows.map((row, idx) => {
              return <LegendRow
                key={`${row.hash}`}
                editable={!locked}
                row={row}
                onEdit={(text) => handleEditItem(idx, text)}
                onDelete={() => handleRemoveItem(idx)}
                onMove={(direction) => handleMoveItem(idx, direction)}
              />;
            })}
          </div>
        </div>
      </div>
    </>
  );
}


interface LegendRowProps {
  editable?: boolean;
  row: LegendNodeRow;
  onEdit: (newText: string) => void;
  onDelete: () => void;
  onMove: (direction: "up" | "down") => void;
}

const rowItemHeight = 36;
const rowItemWidth = 76;

const LegendRow: React.FC<LegendRowProps> = ({ row, onEdit, onDelete, onMove, editable }) => {
  const [editRef, setEditRef] = useState<HTMLButtonElement>();

  return <>
    {editRef &&
      <Popover
        open={Boolean(editRef)}
        anchorEl={editRef}
        onClose={() => setEditRef(undefined)}
        sx={{ width: "556px" }}
      >
        <HidroDXEditor
          startText={row.title}
          onChange={(text) => onEdit(text)}
        />
      </Popover>
    }

    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: "row",
        alignItems: "center",
        justifyContent: 'space-between',
        padding: 0,
      }}>
      <div style={{
        display: 'flex',
        flexDirection: "row",
        alignItems: "center",
      }}>
        {!row.isTable &&
          <div style={{
            textAlign: "center",
            boxSizing: "border-box",
            width: rowItemWidth,
            height: rowItemHeight,
            border: row.border,
            clipPath: row.clipPath,
            backgroundColor: row.bgColor,
            borderRadius: row.borderRadius,
          }} />}

        {row.isTable &&
          <div style={{
            textAlign: "center",
            boxSizing: "border-box",
            width: rowItemWidth,
            height: rowItemHeight,
          }}>
            <TableChart fontSize="large" sx={{
              color: row.bgColor,
              border: row.border,
              clipPath: row.clipPath,
              borderRadius: row.borderRadius,
            }} />
          </div>
        }

        {row.title && <HidroDXEditor
          key={`${objectToHash(row.title)}`}
          readOnly
          startText={row.title}
        />}
      </div>

      {/* Items de edição */}
      {editable &&
        <div style={{
          padding: "5px",
          margin: 0,
          height: '36px',
          backgroundColor: "#000",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "5px",
        }}>
          <Tooltip title="Atualizar Texto" sx={{
            transform: 'scale(0.6)',
            transformOrigin: 'center',
          }} >
            <IconButton
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                setEditRef(ev.currentTarget);
              }}
              size='small'
              sx={{ color: "#FFF" }}
            >
              <Edit fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remover Item" sx={{
            transform: 'scale(0.6)',
            transformOrigin: 'center',
          }} >
            <IconButton
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                onDelete();
              }}
              size='small'
              sx={{ color: "#FFF" }}
            >
              <DeleteForever fontSize='small' />
            </IconButton>
          </Tooltip>

          <div style={{
            display: 'flex',
            flexDirection: "column",
            transform: 'scale(0.6)',
            transformOrigin: 'center',
          }}>
            <IconButton
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                onMove("up");
              }}
              size='small'
              sx={{ color: "#FFF" }}
            >
              <ArrowUpward fontSize='small' />
            </IconButton>

            <IconButton
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                onMove("down");
              }}
              size='small'
              sx={{ color: "#FFF" }}
            >
              <ArrowDownward fontSize='small' />
            </IconButton>
          </div>
        </div>
      }
    </div>

  </>;
};